import { Box, Button, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { IPublication } from '@opengov/stories-shared/types';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';

import { IPublicationModalMessages, IPublicationViewMessages } from '../../../../types';
import { UpdatePublicationModal } from '../UpdatePublicationModal/UpdatePublicationModal';

import { styles } from './Header.styles';

export type IHeaderProps = {
  copyPublicationJobId: string;
  isAdmin: boolean;
  publication: IPublication;
  entityId: number;
  children: React.ReactNode;
  breadcrumbTitle: string;
  isCreateFromTemplate?: boolean;
  messages: IPublicationViewMessages;
  publicationModalMessages: IPublicationModalMessages;
  userId: number;
  navigateToPublicationList(): void;
}

export const Header: React.FC<IHeaderProps> = ({
  copyPublicationJobId,
  children,
  breadcrumbTitle,
  messages,
  navigateToPublicationList,
  publicationModalMessages,
  publication,
  isAdmin,
  entityId,
  userId,
  isCreateFromTemplate = false
}) => {
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    navigateToPublicationList();
  };

  const [isUpdatePublicationModalVisible, setUpdatePublicationModalVisible] = useState(false);

  if (isUpdatePublicationModalVisible) {
    return createPortal(
      <UpdatePublicationModal
        isAdmin={isAdmin}
        entityId={entityId}
        publicationModalMessages={publicationModalMessages}
        publication={publication}
        onClose={() => setUpdatePublicationModalVisible(false)}
        userId={userId}
      />,
      document.getElementById('app')
    );
  }

  if (isCreateFromTemplate) {
    return (
      <Box component="header" data-test="create-from-template-header" sx={styles.rootTemplate}>
        <Typography sx={styles.templateTitle}>{messages.createFromTemplate}</Typography>
        <Box>{children}</Box>
      </Box>
    );
  }

  return (
    <Box component="header" data-test="publications-header" sx={styles.root}>
      <Box sx={styles.navBarBreadcrumb}>
        <nav className="ui-breadcrumbs" data-test="publications-breadcrumbs-navigation">
          <Box
            component="a"
            href="#"
            className="ui-breadcrumbs__item"
            sx={styles.breadcrumbsLink}
            data-test="publications-breadcrumbs-link"
            onClick={handleClick}
          >
            {messages.storiesAndPublications}
          </Box>
          <Typography component="span" className="ui-breadcrumbs__item" sx={styles.titleStyle}>
            {breadcrumbTitle}
          </Typography>
        </nav>
      </Box>
      <Box sx={styles.navBarDetails}>
        <Box sx={styles.leftContent}>
          <Tooltip placement="bottom" title={breadcrumbTitle}>
            <Typography variant="h2">{breadcrumbTitle}</Typography>
          </Tooltip>
          {isAdmin && breadcrumbTitle && (
            <Button
              size="small"
              variant="text"
              disabled={!!copyPublicationJobId}
              aria-hidden={!!copyPublicationJobId || undefined}
              data-test="publications-edit-button"
              startIcon={<EditOutlinedIcon aria-hidden={true} fontSize="small" />}
              onClick={() => setUpdatePublicationModalVisible(true)}
              sx={[styles.editButton, copyPublicationJobId ? styles.hideControls : null]}
            >
              {messages.edit}
            </Button>
          )}
        </Box>
        <Box sx={styles.content}>{children}</Box>
      </Box>
    </Box>
  );
};
